import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["quantity", "quantityHidden", "amount", "fee", "total", "field", "currency", "amountHidden", "feePercentage", "quickButton1", "quickButton2", "quickButton3", "shareStatus", "amountArea", "submitButton"]
  static values = {
    amount: Number,
    roundId: String,
    calculateUrl: String,
    quantityStep: Number,
    skipInitial: Boolean,
    showAmountDecimals: Boolean,
    emulate: Boolean,
    sharePrice: Number,
    feePercentage: Number
  }

  connect() {
    this.backendData = null
    this._pendingAmountNudge = null
    this._amountDirty = false
    this._didInitialAmountLoadAnimation = false
    this._didInitialCurrencyReveal = false
    if (this.hasCurrencyTarget) this.currencyTarget.style.opacity = "0"
    this.updateDisplay()
    this.updateAmountInputFontSize()
    this._onResize = () => this.updateAmountInputFontSize()
    window.addEventListener("resize", this._onResize)

    const focusAmountField = () => {
      if (this.hasFieldTarget) requestAnimationFrame(() => this.fieldTarget.focus())
    }

    // In preview mode skip these so server-rendered state (amount, quantity, errors, pill labels) is not overwritten
    if (this.skipInitialValue) {
      focusAmountField()
    } else {
      // Run initial async work (quick buttons + backend normalize), then focus so focus isn't lost when updateDisplay runs
      const init = []
      init.push(this.initializeQuickButtons().catch(e => console.error('initializeQuickButtons failed:', e)))
      if (this.calculateUrlValue && this.amountValue) {
        init.push(this.callBackendWithAmount(this.amountValue).catch(e => console.error('Initial backend normalization failed:', e)))
      }
      Promise.all(init).then(focusAmountField, focusAmountField)
    }
  }

  disconnect() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize)
      this._onResize = null
    }
    if (this._calculateDebounceTimer) {
      clearTimeout(this._calculateDebounceTimer)
      this._calculateDebounceTimer = null
    }
  }

  // Single handler for input, keyup, and blur on the amount field
  async onAmountChange(event) {
    const field = event.currentTarget

    // Sanitize raw input and keep local state in sync (input events only)
    if (event.type === 'input') {
      const rawValue = field.value
      const { sanitized, cursorOffset } = this.sanitizeAmountInput(rawValue, field.selectionStart)
      if (sanitized !== rawValue) {
        field.value = sanitized
        const newPos = Math.max(0, Math.min(cursorOffset, sanitized.length))
        field.setSelectionRange(newPos, newPos)
      }
      const parsed = this.parseNumber(sanitized)
      const num = parsed != null ? parsed : 0
      this.amountValue = num
      if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = String(num)
      this.updateAmountInputFontSize()
      this._amountDirty = true
    }

    if (event.type === 'blur' || event.type === 'keydown') {
      if (event.type === 'keydown') {
        event.preventDefault()
        event.stopPropagation()
      }
      if (event.type === 'blur' && !this._amountDirty) return
      // Cancel any pending debounce and validate immediately
      if (this._calculateDebounceTimer) {
        clearTimeout(this._calculateDebounceTimer)
        this._calculateDebounceTimer = null
      }
      this._amountDirty = false
      await this._validateAmount()
      return
    }

    // input / keyup: debounce backend call — disable button immediately so the submit
    // stays disabled for the entire 600 ms window, not just during the fetch.
    // Skip keyup for Enter — the keydown already triggered immediate validation.
    if (event.type === 'keyup' && event.key === 'Enter') return
    if (this._calculateDebounceTimer) clearTimeout(this._calculateDebounceTimer)
    this._setSubmitLoading(true)
    this._calculateDebounceTimer = setTimeout(() => {
      this._calculateDebounceTimer = null
      this._amountDirty = false
      this._validateAmount()
    }, 1000)
  }

  // Validate current amount with backend (or emulate)
  async _validateAmount() {
    const num = this.amountValue
    if (this.emulateValue) {
      this.applyEmulatedAmount(num)
      return
    }
    this._setSubmitLoading(true)
    try {
      // Always call backend if URL is available, even for 0, so backend can normalize based on shares_left
      if (this.calculateUrlValue) {
        await this.callBackendWithAmount(num)
      } else {
        // Fallback: round to nearest 1000
        this.amountValue = Math.max(0, Math.round(num / 1000) * 1000)
        if (this.hasFieldTarget) {
          this.fieldTarget.value = this.formatWithThinSpace(this.amountValue)
          if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = String(this.amountValue)
          this.updateAmountInputFontSize()
        }
      }
    } finally {
      this._setSubmitLoading(false)
    }
  }

  // Show/hide a spinner inside the submit button and toggle its disabled state.
  // Stores the button's original inner HTML so it can be restored exactly.
  _setSubmitLoading(loading) {
    if (!this.hasSubmitButtonTarget) return
    const btn = this.submitButtonTarget
    btn.disabled = loading
    const inner = btn.querySelector('span.relative')
    if (!inner) return

    if (loading && !this._submitOriginalHTML) {
      this._submitOriginalHTML = inner.innerHTML
      inner.innerHTML = `<svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`
    } else if (!loading && this._submitOriginalHTML) {
      inner.innerHTML = this._submitOriginalHTML
      this._submitOriginalHTML = null
    }
  }

  // Increase amount
  async increase() {
    this._pendingAmountNudge = "up"
    const changed = await this.adjustAmount('up')
    if (!changed) this._pendingAmountNudge = null
  }

  // Decrease amount
  async decrease() {
    this._pendingAmountNudge = "down"
    const changed = await this.adjustAmount('down')
    if (!changed) this._pendingAmountNudge = null
  }

  // Handle quick amount button clicks
  async setQuickAmount(event) {
    const amount = parseFloat(event.currentTarget.dataset.amount)
    if (amount > 0) {
      await this.callBackendWithAmount(amount)
    }
  }

  // Update all display elements with backend data or current values
  updateDisplay() {
    const data = this.backendData

    if (data) {
      // Parse amount from backend for input field (needs numeric value)
      const amountStr = data.invested_amount
      if (amountStr != null) {
        const parsed = this.parseNumber(amountStr)
        if (parsed != null) this.amountValue = parsed
      }

      // Update input field (amount in euros); 8.7 thin space for display
      if (this.hasFieldTarget) {
        this.fieldTarget.value = this.formatWithThinSpace(this.amountValue)
        if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = String(this.amountValue)
        this.updateAmountInputFontSize()
      }

      // Update display targets (use backend's formatted strings directly)
      if (data.quantity != null) {
        const formattedValue = this.formatNumber(data.quantity)
        if (this.quantityTarget.tagName === 'INPUT') {
          this.quantityTarget.value = data.quantity
        } else {
          this.quantityTarget.textContent = formattedValue
        }
        if (this.hasQuantityHiddenTarget) this.quantityHiddenTarget.value = data.quantity
      }
      if (this.hasFeePercentageTarget && data.fee_percentage != null) this.feePercentageTarget.textContent = data.fee_percentage
      if (data.invested_amount != null) this.amountTarget.textContent = data.invested_amount
      if (data.fee_amount != null) this.feeTarget.textContent = data.fee_amount
      if (data.total != null) this.totalTarget.textContent = data.total
      if (this.hasShareStatusTarget && !this.emulateValue) {
        this.shareStatusTarget.textContent = data.share_status_notification || ""
      }
    } else {
      // No backend data (e.g. preview mode) - update amount input only; leave quantity and share status
      // as server-rendered so preview breakdown and error messages are not overwritten
      if (this.hasFieldTarget) {
        this.fieldTarget.value = this.formatWithThinSpace(this.amountValue)
        if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = String(this.amountValue)
        this.updateAmountInputFontSize()
      }
    }
    this.runPendingAmountNudge()
  }

  // Dynamic font size for amount input (WP4): try max size first, then shrink to fit available width and digit count
  updateAmountInputFontSize() {
    if (!this.hasFieldTarget) return
    const value = this.fieldTarget.value || ""
    const isMobileViewport = window.matchMedia("(max-width: 640px)").matches
    const isNarrowViewport = window.matchMedia("(max-width: 440px)").matches
    const minSize = isNarrowViewport ? 18 : (isMobileViewport ? 24 : 32)
    const shrinkStep = isNarrowViewport ? 2 : 4
    const maxSize = 72
    let size = maxSize
    const field = this.fieldTarget
    const availableWidth = field.clientWidth
    if (availableWidth > 0) {
      const measurer = this._getMeasurer()
      const fieldStyles = window.getComputedStyle(field)
      const currencyText = this.hasCurrencyTarget ? (this.currencyTarget.textContent || "€") : ""
      const currencyGapPx = this.hasCurrencyTarget ? this.currencyGapPx(fieldStyles) : 0
      measurer.style.fontFamily = fieldStyles.fontFamily
      measurer.style.fontWeight = fieldStyles.fontWeight
      measurer.style.letterSpacing = fieldStyles.letterSpacing
      measurer.style.fontSize = `${size}px`
      measurer.textContent = `${value}${currencyText}`
      while ((measurer.offsetWidth + currencyGapPx) > availableWidth && size > minSize) {
        size -= shrinkStep
        measurer.style.fontSize = `${size}px`
      }
    }
    field.style.fontSize = `${size}px`
    if (this.hasCurrencyTarget) this.currencyTarget.style.fontSize = `${size}px`
    this.positionCurrency()
  }

  // Keep "€" visually attached to centered amount while input itself spans full width.
  positionCurrency() {
    if (!this.hasFieldTarget || !this.hasCurrencyTarget) return

    const field = this.fieldTarget
    const currency = this.currencyTarget
    const availableWidth = field.clientWidth
    if (availableWidth <= 0) return

    const value = field.value || ""
    const measurer = this._getMeasurer()
    const fieldStyles = window.getComputedStyle(field)
    measurer.style.fontFamily = fieldStyles.fontFamily
    measurer.style.fontWeight = fieldStyles.fontWeight
    measurer.style.letterSpacing = fieldStyles.letterSpacing
    measurer.style.fontSize = fieldStyles.fontSize
    measurer.textContent = value

    const gapPx = this.currencyGapPx(fieldStyles)
    const fieldLeft = field.offsetLeft
    const centeredValueRightEdge = fieldLeft + (availableWidth / 2) + (measurer.offsetWidth / 2)
    const maxLeft = fieldLeft + availableWidth - currency.offsetWidth
    const left = Math.max(fieldLeft, Math.min(centeredValueRightEdge + gapPx, maxLeft))
    currency.style.left = `${left}px`
    this.revealCurrencyIfNeeded()
    this.runInitialAmountLoadAnimation()
  }

  // Hide only currency until it has been precisely positioned (prevents initial overlap flash).
  revealCurrencyIfNeeded() {
    if (this._didInitialCurrencyReveal || !this.hasCurrencyTarget) return
    this._didInitialCurrencyReveal = true
    const currency = this.currencyTarget
    currency.style.opacity = "1"
    if (typeof currency.animate !== "function") return
    currency.animate(
      [{ opacity: 0 }, { opacity: 1 }],
      {
        duration: 160,
        easing: "cubic-bezier(0, 0, 0.2, 1)"
      }
    )
  }

  // One-time entrance animation for amount area after initial layout stabilizes.
  runInitialAmountLoadAnimation() {
    if (this._didInitialAmountLoadAnimation) return
    const target = this.hasAmountAreaTarget ? this.amountAreaTarget : (this.hasFieldTarget ? this.fieldTarget : null)
    if (!target || typeof target.animate !== "function") {
      this._didInitialAmountLoadAnimation = true
      return
    }

    this._didInitialAmountLoadAnimation = true
    requestAnimationFrame(() => {
      target.animate(
        [
          { opacity: 0.85, transform: "translateY(6px) scale(0.99)" },
          { opacity: 1, transform: "translateY(0) scale(1)" }
        ],
        {
          duration: 240,
          easing: "cubic-bezier(0, 0, 0.2, 1)"
        }
      )
    })
  }

  runPendingAmountNudge() {
    if (!this._pendingAmountNudge) return
    const direction = this._pendingAmountNudge
    this._pendingAmountNudge = null
    this.animateAmountNudge(direction)
  }

  animateAmountNudge(direction) {
    const targets = []
    if (this.hasFieldTarget) targets.push(this.fieldTarget)
    if (this.hasCurrencyTarget) targets.push(this.currencyTarget)
    if (targets.length === 0 && this.hasAmountAreaTarget) targets.push(this.amountAreaTarget)
    if (targets.length === 0) return

    const isUp = direction === "up"
    const keyframes = isUp
      ? [
          { transform: "translateY(2px) scale(0.96)", opacity: 0.9 },
          { transform: "translateY(-1px) scale(1.035)", opacity: 1, offset: 0.55 },
          { transform: "translateY(0) scale(1)", opacity: 1 }
        ]
      : [
          { transform: "translateY(-2px) scale(1.04)", opacity: 0.9 },
          { transform: "translateY(1px) scale(0.965)", opacity: 1, offset: 0.55 },
          { transform: "translateY(0) scale(1)", opacity: 1 }
        ]

    targets.forEach((target) => {
      if (typeof target.animate !== "function") return
      target.animate(
        keyframes,
        {
          duration: 210,
          easing: "cubic-bezier(0.236, 0, 0.236, 1)"
        }
      )
    })
  }

  // Keep visible distance between amount and currency across dynamic font sizes.
  currencyGapPx(fieldStyles = null) {
    const styles = fieldStyles || window.getComputedStyle(this.fieldTarget)
    const fontSize = parseFloat(styles.fontSize) || 0
    const isMobileViewport = window.matchMedia("(max-width: 640px)").matches
    const isNarrowViewport = window.matchMedia("(max-width: 440px)").matches
    const ratio = isNarrowViewport ? 0.4 : (isMobileViewport ? 0.35 : 0.25)
    const minGap = isNarrowViewport ? 12 : (isMobileViewport ? 10 : 6)
    const extraGap = isNarrowViewport ? 3 : (isMobileViewport ? 2 : 0)
    return Math.max(minGap, Math.round(fontSize * ratio)) + extraGap
  }

  // Lazily create an offscreen span with the same font properties for text measurement
  _getMeasurer() {
    if (!this._measurerEl) {
      const el = document.createElement("span")
      el.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;font-family:inherit;font-weight:inherit;letter-spacing:-2px"
      this.element.appendChild(el)
      this._measurerEl = el
    }
    return this._measurerEl
  }

  // Call backend API with amount and update UI
  async callBackendWithAmount(amount) {
    if (this.emulateValue) {
      this.applyEmulatedAmount(amount)
      return
    }
    if (!this.calculateUrlValue) return

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          amount: amount,
          round_id: this.roundIdValue
        })
      })

      const data = await response.json()

      if (data.errors && Object.keys(data.errors).length > 0) {
        console.error('Validation errors:', data.errors)
      }

      this.backendData = data
      this.updateDisplay()
    } catch (error) {
      console.error('Error calling backend:', error)
      // Fallback: use rounded client-side value
      this.amountValue = Math.max(0, Math.round(amount / 1000) * 1000)
      if (this.hasFieldTarget) {
        this.fieldTarget.value = this.formatWithThinSpace(this.amountValue)
        if (this.hasAmountHiddenTarget) this.amountHiddenTarget.value = String(this.amountValue)
        this.updateAmountInputFontSize()
      }
    }
  }

  // Adjust amount up/down (for +/- buttons)
  async adjustAmount(direction) {
    if (this.emulateValue) {
      this.adjustAmountEmulated(direction)
      return true
    }
    if (!this.backendData || !this.calculateUrlValue) return false

    const currentQuantity = this.backendData.quantity || 0
    const step = this.hasQuantityStepValue && this.quantityStepValue > 0 ? this.quantityStepValue : 1
    const nudgedQuantity = direction === 'up'
      ? currentQuantity + step
      : Math.max(0, currentQuantity - step)

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          quantity: nudgedQuantity,
          round_id: this.roundIdValue
        })
      })

      const data = await response.json()
      this.backendData = data
      this.updateDisplay()
      return true
    } catch (error) {
      console.error('Error adjusting amount:', error)
      return false
    }
  }

  // Format number for display (Finnish locale) — used for quantity, etc.
  formatNumber(value) {
    return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 2 }).format(value)
  }

  // Format amount: hide decimals when whole (e.g. 20 160 not 20 160,00)
  formatAmount(value) {
    return new Intl.NumberFormat('fi-FI', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value)
  }

  // 8.7: Format amount for the live input — thin space (U+2006) thousands; decimals only when needed (hide ,00 for whole euros)
  formatWithThinSpace(value) {
    const n = Math.max(0, Number(value))
    if (n === 0) return ""
    const isWhole = n % 1 === 0
    if (this.showAmountDecimalsValue && !isWhole) {
      const fixed = n.toFixed(2)
      const [intPart, decPart] = fixed.split(".")
      const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "\u2006")
      return `${intFormatted},${decPart}`
    }
    const intPart = Math.round(n).toString()
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "\u2006")
  }

  // Parse number from input (strips spaces, thin space U+2006, comma/period)
  parseNumber(value) {
    if (!value) return null
    const cleaned = value.toString().replace(/\s/g, "").replace(/\u2006/g, "").replace(",", ".")
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? null : Math.max(0, parsed)
  }

  // Restrict input to digits and one decimal separator (comma or period). Returns { sanitized, cursorOffset } for cursor restore.
  sanitizeAmountInput(value, selectionStart) {
    if (!value) return { sanitized: "", cursorOffset: 0 }
    const maxSymbols = 15
    let sanitized = ""
    let separatorSeen = false
    let cursorOffset = 0
    const str = value.toString()
    for (let i = 0; i < str.length; i++) {
      const c = str[i]
      if (c >= "0" && c <= "9") {
        if (sanitized.length >= maxSymbols) continue
        sanitized += c
        if (i < selectionStart) cursorOffset = sanitized.length
      } else if ((c === "," || c === ".") && !separatorSeen) {
        if (sanitized.length >= maxSymbols) continue
        separatorSeen = true
        sanitized += c
        if (i < selectionStart) cursorOffset = sanitized.length
      }
    }
    return { sanitized, cursorOffset }
  }

  // Initialize quick amount buttons once
  async initializeQuickButtons() {
    if (!this.calculateUrlValue) return
    if (!this.hasQuickButton1Target || !this.hasQuickButton2Target || !this.hasQuickButton3Target) return

    const quickConfigs = [
      { baseAmount: 5000, button: this.quickButton1Target },
      { baseAmount: 15000, button: this.quickButton2Target },
      { baseAmount: 20000, button: this.quickButton3Target }
    ]

    const fetchOpts = {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
    };

    await Promise.all(quickConfigs.map(async ({ baseAmount, button }) => {
      try {
        const response = await fetch(this.calculateUrlValue, {
          ...fetchOpts,
          body: JSON.stringify({ amount: baseAmount, round_id: this.roundIdValue })
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        const displayAmount = this.extractAmountDisplay(data)
        const numericAmount = this.extractAmountNumber(data)

        button.textContent = displayAmount
        if (numericAmount > 0) {
          button.dataset.amount = numericAmount
        }
      } catch (error) {
        console.error(`Failed to initialize quick button for ${baseAmount}:`, error)
        // Fallback: use thin-space formatting to match backend/V2
        button.textContent = `${this.formatWithThinSpace(baseAmount)} €`
        button.dataset.amount = baseAmount
      }
    }))
  }

  // Extract numeric amount from backend payload
  extractAmountNumber(payload) {
    const str = payload?.invested_amount
    const parsed = this.parseNumber(str)
    return parsed == null ? 0 : parsed
  }

  // Use backend's formatted string (thin-space thousands) when present; fallback to formatAmount for "0 €" or parse errors
  extractAmountDisplay(payload) {
    const str = payload?.invested_amount
    if (!str) return '0 €'
    return `${str} €`
  }

  // ── Emulated backend (Lookbook state previews) ─────────────────────────────

  formatMoneyEur(value) {
    const n = Number(value)
    if (isNaN(n)) return "0"
    // Hide decimals when whole (use case: display amounts without ,00 when not needed)
    return n.toLocaleString('fi-FI', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  formatFeePercentageDisplay(value) {
    const n = Number(value)
    if (isNaN(n)) return "0"
    return n % 1 === 0 ? String(n) : n.toFixed(1).replace('.', ',')
  }

  applyEmulatedAmount(amount) {
    const sharePrice = Number(this.sharePriceValue)
    const feePct = Number(this.feePercentageValue) || 0
    if (!(sharePrice > 0)) return

    const quantity = Math.round(amount / sharePrice)
    const investedAmountEur = quantity * sharePrice
    const feeAmount = investedAmountEur * (feePct / 100)
    const total = investedAmountEur + feeAmount

    this.amountValue = investedAmountEur
    this.backendData = {
      quantity,
      invested_amount: this.formatMoneyEur(investedAmountEur),
      fee_percentage: this.formatFeePercentageDisplay(feePct),
      fee_amount: this.formatMoneyEur(feeAmount),
      total: this.formatMoneyEur(total),
      share_status_notification: ""
    }
    this.updateDisplay()
  }

  adjustAmountEmulated(direction) {
    const sharePrice = Number(this.sharePriceValue)
    const step = this.hasQuantityStepValue && this.quantityStepValue > 0 ? this.quantityStepValue : 1
    if (!(sharePrice > 0)) return

    const currentQuantity = this.backendData?.quantity ?? Math.round(this.amountValue / sharePrice)
    const nudgedQuantity = direction === 'up'
      ? currentQuantity + step
      : Math.max(0, currentQuantity - step)
    const amount = nudgedQuantity * sharePrice
    this.applyEmulatedAmount(amount)
  }

}
